import {
  CalendarBiLogger,
  createCalendarBiLogger,
} from '../bi/biLoggerFactory';
import { BusinessInfo, BusinessInfoBase } from '@wix/bookings-uou-types';
import { GetActiveFeaturesResponse } from '@wix/ambassador-services-catalog-server/types';
import {
  CalendarState,
  TFunction,
} from '../../components/BookingCalendar/controller';
import { CalendarApi } from '../../api/CalendarApi';
import {
  BookingsQueryParams,
  WixOOISDKAdapter,
} from '@wix/bookings-adapter-ooi-wix-sdk';
import { ControllerFlowAPI, ReportError } from '@wix/yoshi-flow-editor';
import Experiments from '@wix/wix-experiments';
import { Preset } from '../../types/types';
import { ISettingsParams } from '../../components/BookingCalendar/settingsParams';

export type GetContentParams = {
  settingsParam: any;
  translationKey: string;
  options?: object;
};

export type GetContent = (params: GetContentParams) => string;

export type CalendarContext = {
  wixSdkAdapter: WixOOISDKAdapter;
  biLogger: CalendarBiLogger;
  businessInfo?: BusinessInfoBase;
  isPricingPlanInstalled: boolean;
  isMemberAreaInstalled: boolean;
  isMobile: boolean;
  activeFeatures?: GetActiveFeaturesResponse;
  calendarApi: CalendarApi;
  settings: ControllerFlowAPI['settings'];
  experiments: Experiments;
  reportError: ReportError;
  t: TFunction;
  getContent: GetContent;
  settingsParams: Record<keyof ISettingsParams, any>;
  preset: Preset;
  isDayful: boolean;
  hasReferralParam: boolean;
  isCartEnabled: boolean;
};

export async function createCalendarContext({
  flowAPI,
  businessInfo,
  activeFeatures,
  calendarApi,
  wixSdkAdapter,
  initialState,
  isPricingPlanInstalled,
  isMemberAreaInstalled,
  settingsParams,
  preset,
  isDayful,
}: {
  flowAPI: ControllerFlowAPI;
  businessInfo?: BusinessInfo;
  activeFeatures?: GetActiveFeaturesResponse;
  calendarApi: CalendarApi;
  wixSdkAdapter: WixOOISDKAdapter;
  initialState: CalendarState;
  isPricingPlanInstalled: boolean;
  isMemberAreaInstalled: boolean;
  settingsParams: any;
  preset: Preset;
  isDayful: boolean;
}): Promise<CalendarContext> {
  const {
    settings,
    translations: { t },
    reportError,
    experiments,
    environment: { isMobile },
  } = flowAPI;
  const biLogger = createCalendarBiLogger(
    flowAPI,
    initialState,
    wixSdkAdapter,
    settings,
    settingsParams,
    preset,
    businessInfo,
  );
  const getContent = ({
    settingsParam,
    translationKey,
    options = {},
  }: GetContentParams) =>
    settings.get(settingsParam) || t(translationKey, options);

  const hasReferralParam = !!wixSdkAdapter.getUrlQueryParamValue(
    BookingsQueryParams.REFERRAL,
  );

  const isCartEnabled =
    experiments.enabled('specs.bookings.cart') && businessInfo
      ? await wixSdkAdapter.isCartEnabled(businessInfo!)
      : false;

  return {
    wixSdkAdapter,
    biLogger,
    businessInfo,
    activeFeatures,
    isPricingPlanInstalled,
    isMemberAreaInstalled,
    calendarApi,
    settings,
    experiments,
    reportError,
    t,
    getContent,
    isMobile,
    settingsParams,
    preset,
    isDayful,
    hasReferralParam,
    isCartEnabled,
  };
}
